// Sound engine — Web Audio API
// 
// Browser rule: audio requires a user gesture before it can play.
// All sounds here are triggered by button clicks, which satisfy that requirement.
// The boot sound on page load is intentionally removed — browsers block it.
//
// Sound map:
//   sounds.valid()    — played when user submits a Valid verdict
//   sounds.mid()      — played when user submits a Mid verdict
//   sounds.streak()   — played after 5 consecutive Valid verdicts
//   sounds.dismiss()  — played when a profile card is skipped
//   sounds.milestone(n) — played when user hits 10, 25, 50 ratings given

let audioCtx = null;
let enabled  = localStorage.getItem('sound_enabled') !== 'false';

function ctx() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (can happen after inactivity)
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

function tone({ freq = 440, type = 'sine', duration = 0.15, vol = 0.25, delay = 0 } = {}) {
  if (!enabled) return;
  try {
    const c   = ctx();
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);

    osc.type = type;
    osc.frequency.setValueAtTime(freq, c.currentTime + delay);

    gain.gain.setValueAtTime(0.001, c.currentTime + delay);
    gain.gain.linearRampToValueAtTime(vol, c.currentTime + delay + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + delay + duration);

    osc.start(c.currentTime + delay);
    osc.stop(c.currentTime + delay + duration + 0.05);
  } catch (_) {
    // Silently fail — audio unavailable
  }
}

export const sounds = {
  // Valid — bright ascending two-note chime
  valid: () => {
    tone({ freq: 523, type: 'triangle', duration: 0.08, vol: 0.2 });
    tone({ freq: 784, type: 'triangle', duration: 0.14, vol: 0.25, delay: 0.09 });
  },

  // Mid — flat single deflating tone
  mid: () => {
    tone({ freq: 300, type: 'sine', duration: 0.12, vol: 0.18 });
    tone({ freq: 220, type: 'sine', duration: 0.18, vol: 0.12, delay: 0.1 });
  },

  // Streak — three-note ascending run, triggered after 5 consecutive valids
  streak: () => {
    tone({ freq: 523, type: 'triangle', duration: 0.1, vol: 0.2 });
    tone({ freq: 659, type: 'triangle', duration: 0.1, vol: 0.22, delay: 0.1 });
    tone({ freq: 784, type: 'triangle', duration: 0.18, vol: 0.28, delay: 0.2 });
  },

  // Dismiss — soft low thud
  dismiss: () => {
    tone({ freq: 140, type: 'sine', duration: 0.1, vol: 0.15 });
  },

  // Milestone — fanfare for rating count milestones (10, 25, 50...)
  milestone: () => {
    tone({ freq: 440, type: 'triangle', duration: 0.1, vol: 0.2 });
    tone({ freq: 554, type: 'triangle', duration: 0.1, vol: 0.22, delay: 0.1 });
    tone({ freq: 659, type: 'triangle', duration: 0.1, vol: 0.24, delay: 0.2 });
    tone({ freq: 880, type: 'triangle', duration: 0.22, vol: 0.28, delay: 0.3 });
  },
};

export function isSoundEnabled() { return enabled; }

export function setSoundEnabled(value) {
  enabled = value;
  localStorage.setItem('sound_enabled', value ? 'true' : 'false');
}

export function toggleSound() {
  setSoundEnabled(!enabled);
  return enabled;
}
